import I18n from 'i18n-js';
import React, { useContext, useState } from 'react';
import { keys, filter, includes } from 'lodash';
import classNames from 'classnames';

import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { gql } from '@reverbdotcom/commons/src/gql';
import { Core_SellForm_FetchShippingEstimatesQuery } from '@reverbdotcom/commons/src/gql/graphql';
import { RCText, RCTextWithIcon, RCImage } from '@reverbdotcom/cadence/components';
import { parseAmount } from '@reverbdotcom/commons/src/money';
import { ThumbsUpIcon } from '@reverbdotcom/cadence/icons/react';

import { SellFormContext } from '../../SellFormContext';
import { packageEntryComplete } from '../../../../discovery/shipping_labels/shipping_label_package';
import {
  carrierCalculatedPackageValidator,
  oversizePackageWarningKey,
  isOversizedDimensionsLabel,
  avoidAdditionalCarrierChargesLabel,
  REGULAR_SIZED_LONGEST_EDGE_THRESHOLD,
  OVERSIZED_LONGEST_EDGE_THRESHOLD,
} from './carrierCalculatedPackageValidator';
import { Field } from '../../sellFormReducerTypes';
import UpsLogo from '../../../../../../images/icons/shipping-icons/ups.svg';
import UspsStackedLogo from '../../../../../../images/icons/shipping-icons/usps-stacked.svg';
import FedExLogo from '../../../../../../images/icons/shipping-icons/fedex.svg';

type ShippingEstimates = NonNullable<NonNullable<Core_SellForm_FetchShippingEstimatesQuery['shippingEstimates']>['estimates']>;

export default function CarrierCalculatedEstimatorV2() {
  const { listingEntry, shopConfig } = useContext(SellFormContext);
  const [shippingEstimatesResponse, setShippingEstimatesResponse] = useState<ShippingEstimates>([]);

  const packageEntry = listingEntry[Field.CARRIER_CALCULATED_PACKAGE];
  const carrierCalculatedPackageInvalidKey = carrierCalculatedPackageValidator(packageEntry)?.errorTranslationKey;
  const validPackageEntry = packageEntryComplete(packageEntry) && !carrierCalculatedPackageInvalidKey;
  const currentPriceInput = listingEntry[Field.PRICE]?.amount ?? '';
  const currentPriceCents = parseAmount(currentPriceInput, shopConfig.currencySetting).amountCents;
  const validPriceAndCurrency = shopConfig.currencySetting === 'USD' &&
                                !!currentPriceInput &&
                                currentPriceCents > 0;
  const shouldFetchEstimate = validPriceAndCurrency && validPackageEntry;
  const showComponent = validPriceAndCurrency && !carrierCalculatedPackageInvalidKey;
  const isOversizedDimensions = oversizePackageWarningKey(packageEntry) === isOversizedDimensionsLabel;
  const isAdditionalCarrierCharges = oversizePackageWarningKey(packageEntry) === avoidAdditionalCarrierChargesLabel;

  const overrideComparisonPackageDimension = () => {
    if (!shouldFetchEstimate) return {};
    const longestSideKey = filter(keys(packageEntry), (key) => includes(['length', 'width', 'height'], key)).sort((a, b) => packageEntry[b]?.value - packageEntry[a]?.value)[0];

    return { [longestSideKey]: { value: `${isOversizedDimensions ? REGULAR_SIZED_LONGEST_EDGE_THRESHOLD : OVERSIZED_LONGEST_EDGE_THRESHOLD}`, unit: 'in' } };
  };

  const userInputPackageDetails = {
    packageEntry,
    itemPrice: {
      amount: currentPriceInput,
      currency: shopConfig.currencySetting,
    },
    categoryRootUuid: listingEntry[Field.ROOT_CATEGORY_UUID],
    categoryUuids: listingEntry[Field.SUBCATEGORY_LEAF_UUIDS],
  };
  const comparisonPackageDetails = {
    packageEntry: {
      ...packageEntry,
      ...overrideComparisonPackageDimension(),
    },
    itemPrice: {
      amount: currentPriceInput,
      currency: shopConfig.currencySetting,
    },
    categoryRootUuid: listingEntry[Field.ROOT_CATEGORY_UUID],
    categoryUuids: listingEntry[Field.SUBCATEGORY_LEAF_UUIDS],
  };

  const { loading } = useQuery(
    FETCH_SHIPPING_ESTIMATES,
    {
      skip: !shouldFetchEstimate,
      variables: {
        input: {
          packageDetails: [
            userInputPackageDetails,
            comparisonPackageDetails,
          ],
        },
      },
      onCompleted: ({ shippingEstimates }) => setShippingEstimatesResponse(shippingEstimates?.estimates ?? []),
    },
  );

  const estimateAvailable = !loading && shippingEstimatesResponse?.[0]?.priceLow?.display;
  const userInputEstimate = shippingEstimatesResponse?.[0];
  const comparisonEstimate = shippingEstimatesResponse?.[1];

  const carrierLogos = [FedExLogo, UpsLogo, UspsStackedLogo];
  const innerContainerClassNames = classNames('d-flex fx-justify-center fx-align-center gap-6 tablet-fx-dir-col tablet-gap-3', { 'tablet-fx-align-start': shouldFetchEstimate && estimateAvailable });

  if (!showComponent) { return null; }

  return (
    <>
      <div className="d-flex fx-dir-col mt-4 bg-color-on-primary padding-6 tablet-padding-4 bd-radius-md gap-3">
        <div className={innerContainerClassNames}>
          <div className="d-flex fx-justify-center fx-align-center gap-4 tablet-fx-order-2">
            {carrierLogos.map((carrierLogo, idx) => {
              return (
                <div
                  key={idx}
                  className="padding-2"
                  style={{
                    width: '5.8rem',
                    maxHeight: '5.8rem',
                    backgroundColor: 'var(--rc-color-palette-bw-white)',
                    borderRadius: 'var(--rc-border-radius-md)',
                  }}
                >
                  <RCImage src={carrierLogo} borderRadius="md" />
                </div>
              );
            })}
          </div>
          {shouldFetchEstimate && estimateAvailable && (
            <div className="d-flex fx-dir-col ptb-3 gap-2 tablet-fx-order-1">
              <p className="mb-half">
                {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.buyerCostHint')}
              </p>
              <div className="d-flex fx-align-center tablet-fx-dir-col tablet-fx-align-start tablet-gap-2">
                <div className="mr-2 size-120 weight-bold ws-nowrap">
                  {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.buyerCostRange', {
                    lowDisplay: userInputEstimate.priceLow.display,
                    highDisplay: userInputEstimate.priceHigh.display,
                  })}
                </div>
                {isOversizedDimensions && (
                  <div
                    className="padding-2 bd-radius-md bd-1 size-90 mobile-mt-2"
                    style={{
                      backgroundColor: 'var(--rc-color-background-notice-ultralight-opaque)',
                      borderColor: 'var(--rc-color-accent-notice)',
                    }}
                  >
                    <span className="weight-bold mr-1">
                      {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.highPrice')}
                    </span>
                    <span>
                      {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.reduceBulging')}
                    </span>
                  </div>
                )}
                {isAdditionalCarrierCharges && (
                  <div
                    className="padding-2 bd-radius-md bd-1 size-90 mobile-mt-2"
                    style={{
                      backgroundColor: 'var(--rc-color-background-success-ultralight)',
                      borderColor: 'var(--rc-color-accent-success)',
                    }}
                  >
                    <RCTextWithIcon icon={<ThumbsUpIcon />} placement="left" size="large">
                      <span className="weight-bold mr-1">
                        {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.greatPrice')}
                      </span>
                      <span>
                        {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.buyersSave')}
                      </span>
                    </RCTextWithIcon>
                  </div>
                )}
              </div>
              {isOversizedDimensions && (
                <div>
                  <RCText.Utility size="350" htmlTag="span">
                    {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.standardBoxCostHint')}
                  </RCText.Utility>
                  <RCText.Utility size="350" weight="bold" htmlTag="span">
                    {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.buyerCostRange', {
                      lowDisplay: comparisonEstimate.priceLow.display,
                      highDisplay: comparisonEstimate.priceHigh.display,
                    })}
                  </RCText.Utility>
                </div>
              )}
            </div>
          )}
        </div>
        {!shouldFetchEstimate && (
          <div className="d-flex fx-justify-center weight-semibold" style={{ fontSize: 'var(--rc-font-size-450)' }}>
            {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.buyerPay')}
          </div>
        )}
        <div className={classNames('d-flex size-90', { 'fx-justify-center': !shouldFetchEstimate })}>
          {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.shipWith')}
        </div>
      </div>
    </>
  );
}

export const FETCH_SHIPPING_ESTIMATES = gql(`
  query Core_SellForm_FetchShippingEstimates($input: InputShippingEstimatesRequest!) {
    shippingEstimates(input: $input) {
      estimates {
        priceLow {
          amountCents
          display
        }
        priceHigh {
          amountCents
          display
        }
      }
    }
  },
`);
